import React, { type ReactNode } from 'react';
import { Text } from '../../atoms/text';

interface PageFiltersBarProps {
  /** Filter controls rendered inside the shared surface. */
  children: ReactNode;
  /** Optional classes applied to the outer filter surface. */
  className?: string;
  /** Optional classes appended to the shared responsive content layout. */
  contentClassName?: string;
  /** Accessible validation content associated with a filter control. */
  validationMessage?: ReactNode;
  /** DOM identifier used by the invalid control's `aria-describedby`. */
  validationId?: string;
  /** Optional content displayed at the end of the validation row. */
  validationEnd?: ReactNode;
}

/**
 * Shared responsive surface for page-level search and filter controls.
 *
 * The surface owns cross-page spacing, wrapping, and top alignment. Pages with
 * multi-row or breakpoint-specific controls should compose that layout inside
 * one full-width child instead of replacing the shared surface contract.
 * Validation renders in a separate conditional row so one invalid control
 * never vertically offsets its siblings or leaves unused space when valid.
 */
export const PageFiltersBar: React.FC<PageFiltersBarProps> = ({
  children,
  className = '',
  contentClassName = '',
  validationMessage,
  validationId,
  validationEnd,
}) => {
  const showValidationRow =
    validationMessage !== undefined || validationEnd !== undefined;

  return (
    <div className={`border-b bg-white ${className}`}>
      <div
        className={`flex flex-col items-stretch gap-4 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-start ${contentClassName}`}
      >
        {children}
      </div>

      {showValidationRow ? (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1 px-4 pb-3">
          <div
            id={validationMessage !== undefined ? validationId : undefined}
            role={validationMessage !== undefined ? 'alert' : undefined}
            aria-live={validationMessage !== undefined ? 'polite' : undefined}
            className="min-w-0 flex-1 basis-[16rem]"
          >
            {validationMessage !== undefined ? (
              <Text size="tiny" color="danger" className="leading-5">
                {validationMessage}
              </Text>
            ) : null}
          </div>

          {validationEnd !== undefined ? (
            <div className="ml-auto shrink-0">{validationEnd}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
